/**
 * Oracle Forms-style function keys for every screen, without per-screen wiring.
 *
 * Screens already render their actions as toolbar buttons labelled with a key cap (`<span class="kbd">F10</span>`).
 * One window-level handler maps a function key to the enabled button carrying that key cap inside the workspace and
 * clicks it, so the advertised shortcut and the button always do exactly the same thing (same permission and
 * disabled rules). Keys that would otherwise reload, search or open browser UI (F3, F5, F7…) are swallowed on ERP
 * screens even when the current screen has no such action, so a stray F5 never throws away a half-entered voucher.
 *
 * F9 stays with the focused field (LOV fields open their list and stop the event before it reaches the window).
 */
export const FORM_KEYS=['F3','F4','F5','F6','F7','F8','F10','F11','F12'] as const;
export type FormKey=typeof FORM_KEYS[number];

/** Keys the browser would act on destructively or confusingly; never passed through on an ERP screen. */
const SWALLOW=new Set<string>(['F3','F4','F5','F6','F7','F8','F10','F12']);

export function isFormKey(key:string):key is FormKey{
 return (FORM_KEYS as readonly string[]).includes(key);
}

function enabled(button:HTMLButtonElement){
 return !button.disabled&&button.getAttribute('aria-disabled')!=='true'&&button.getClientRects().length>0;
}

/** Enabled, visible toolbar button whose key cap reads `key` (e.g. "F10"), searched inside `root`. */
export function actionButtonFor(root:ParentNode,key:string):HTMLButtonElement|null{
 const caps=[...root.querySelectorAll<HTMLElement>('button .kbd, button kbd')];
 for(const cap of caps){
  if(cap.textContent?.trim().toUpperCase()!==key)continue;
  const button=cap.closest('button');
  if(button&&enabled(button))return button;
 }
 return null;
}

/** Function keys available right now (for the shortcut strip). */
export function availableKeys(root:ParentNode):FormKey[]{
 return FORM_KEYS.filter(key=>actionButtonFor(root,key)!==null);
}

type KeyEventLike={
 key:string;ctrlKey:boolean;altKey:boolean;metaKey:boolean;shiftKey:boolean;defaultPrevented:boolean;
 target:EventTarget|null;preventDefault():void;
};

/**
 * Handle one keydown. Returns the clicked button (if any). A modal dialog or LOV owns the keyboard while open, so
 * nothing is triggered behind it. Modified keys (Ctrl+F5 etc.) are left to the browser.
 */
export function handleFormKey(event:KeyEventLike,workspace:ParentNode|null):HTMLButtonElement|null{
 if(event.defaultPrevented||event.ctrlKey||event.altKey||event.metaKey)return null;
 if(!isFormKey(event.key))return null;
 const target=event.target instanceof Element?event.target:null;
 if(target?.closest('dialog,[role=dialog],.erp-modal-wrap'))return null;
 if(typeof document!=='undefined'&&document.querySelector('dialog[open],.erp-modal-wrap'))return null;
 if(SWALLOW.has(event.key))event.preventDefault();
 if(!workspace)return null;
 const button=actionButtonFor(workspace,event.key);
 if(!button)return null;
 event.preventDefault();
 // Let a field commit its value (blur/change) before the action reads the form.
 if(target instanceof HTMLElement&&target!==button)target.blur?.();
 button.click();
 return button;
}
